import { Col, notification, Progress } from 'antd';
import { CancelButton, ConfirmButton } from 'components/common/DefaultFormButton';
import DefaultModal from 'components/common/DefaultModal';
import { InputComponent } from 'components/common/Input';
import { ICheckboxPropsInCheckboxGroup } from 'components/common/Input/new/types';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import { ErrorMessageTextStyled } from 'components/ErrorContainer/styled';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { FORMAT_DATE_TIME_FOR_DISPLAY } from 'constants/days';
import { Form, Formik } from 'formik';
import useUploadTransactionAppointmentFile from 'hooks/useUploadTransactionAppointmentFile';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { IAppointment } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { DocumentSubType, IFile } from 'types/file';
import { ITransaction } from 'types/transaction';
import { getFileUrl } from 'utils';
import * as Yup from 'yup';

import { ModalType } from './types';

const documentSchema = Yup.object({
  description: Yup.string(),
  notes: Yup.string(),
  file: Yup.array().required('Required'),
});

const DocumentModal: React.FC<{
  modalType?: ModalType;
  closeModal: GeneralFunction;
  appointments?: IAppointment[];
  transaction?: ITransaction;
  file?: IFile;
  refetchTransactionData?: GeneralFunction;
}> = ({ modalType, closeModal, appointments, transaction, file, refetchTransactionData }) => {
  const [loading, setLoading] = useState(false);
  const modalVisible = modalType?.includes('Document');
  const isEditModal = modalType?.includes('edit');

  // const uploadPercentageThreshold = 75;

  // useEffect(() => {
  //   const fileUploadListener = (data: any) => {
  //     if (uploadPercentage < uploadPercentageThreshold)
  //       setUploadPercentage(data >= uploadPercentageThreshold ? uploadPercentageThreshold : data);
  //   };
  //   uploadFileProgressEvent.addListener(UPLOAD_FILE, fileUploadListener);
  //   return () => {
  //     uploadFileProgressEvent.removeListener(UPLOAD_FILE, fileUploadListener);
  //   };
  // }, []);

  // useEffect(() => {
  //   let timer: any;
  //   const maxTime = 500;
  //   const minTime = 200;
  //   const maxValue = 5;
  //   const minValue = 3;
  //   if (uploadPercentage >= uploadPercentageThreshold && uploadPercentage <= 95) {
  //     timer = setTimeout(() => {
  //       if (uploadPercentage === 0) clearTimeout(timer);
  //       setUploadPercentage((c) => c + Math.floor(Math.random() * (maxValue - minValue) + minValue));
  //     }, Math.random() * (maxTime - minTime) + minTime);
  //   }
  //   return () => {
  //     clearTimeout(timer);
  //   };
  // }, [uploadPercentage]);

  const formInitValues = {
    description: file?.details.description,
    notes: file?.details.notes,
    file: file
      ? [
          {
            uid: file.id,
            name: file.name,
            url: getFileUrl(file.id),
          },
        ]
      : undefined,
  };

  const documentFormFields: InputProps[] = [
    {
      name: 'description',
      label: 'Document Description',
    },
    {
      name: 'notes',
      label: 'Notes',
      type: INPUT_TYPE.TEXT_AREA,
    },
    {
      name: 'file',
      type: INPUT_TYPE.FILE,
      label: 'Document',
      inputSettings: {
        accept: 'image/*, .pdf',
        multiple: false,
        maxCount: 1,
        singleFileOnly: true,
      },
    },
  ];

  const checkboxProps: ICheckboxPropsInCheckboxGroup[] = useMemo(
    () =>
      appointments?.map((app, index) => {
        const date = moment(app?.date || '').format(FORMAT_DATE_TIME_FOR_DISPLAY);
        return {
          name: app.id,
          label: `Appointment ${index + 1} - ${date}`,
          value: app.files?.some((f) => f.id === file?.id),
        };
      }) || [],
    [appointments, file],
  );

  const onUploadSuccess = () => {
    setLoading(false);
    refetchTransactionData?.();
    closeModal();
    notification.success({
      message: 'Upload document successfully',
      duration: 2,
    });
  };

  const onUploadFailure = () => {
    setLoading(false);
    notification.error({
      message: 'Upload failed! Please try again',
      duration: 2,
    });
  };
  const { uploadDocument, uploadPercentage } = useUploadTransactionAppointmentFile();
  const onSubmitForm = (v: any) => {
    setLoading(true);
    const uploadDocumentData = {
      details: { description: v.description, notes: v.notes },
      files: v.file,
      transactionId: transaction?.id || '',
      subType: DocumentSubType.COMMON,
    };

    const appointmentIds = Object.entries(v.appointments || {})
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);
    uploadDocument({
      callbacks: {
        fail: onUploadFailure,
        success: onUploadSuccess,
      },
      appointmentIds,
      ...uploadDocumentData,
    });

    // dispatch(
    //   FileAction.uploadDocumentStart(
    //     {
    //       ...uploadDocumentData,
    //       ...(appointmentIds.length > 0 ? { appointmentIds: JSON.stringify(appointmentIds) } : {}),
    //     },
    //     { callbackOnSuccess: onUploadSuccess, callbackOnFailure: onUploadFailure },
    //   ),
    // );
  };

  return (
    <DefaultModal
      props={{
        visible: modalVisible,
        title: `${isEditModal ? 'Edit' : 'Add'} Document`,
        onCancel: closeModal,
        destroyOnClose: true,
      }}>
      <Formik
        initialValues={formInitValues}
        name='basic'
        onSubmit={onSubmitForm}
        validationSchema={documentSchema}
        enableReinitialize
        validateOnBlur>
        <Form className='gx-signin-form gx-form-row0'>
          <InputFieldsGenerator formFields={documentFormFields} />
          {loading && (
            <div style={{ padding: '10px 16px' }}>
              <Progress percent={uploadPercentage} strokeColor='#038fde' />
            </div>
          )}
          {(transaction?.appointment?.length || 0) > 0 ? (
            <InputComponent
              style={{ padding: '0 16px', marginTop: 10 }}
              name='appointments'
              label='Use Document with Appointment'
              labelStyle={{ fontWeight: 500, marginBottom: 10 }}
              labelType={LABEL_TYPE.above}
              type={INPUT_TYPE.CHECKBOX_GROUP}
              checkboxGroupProps={checkboxProps}
            />
          ) : (
            <Col span={24} className='gx-pb-2'>
              <ErrorMessageTextStyled>No Appointment to link Documents with.</ErrorMessageTextStyled>
            </Col>
          )}
          <div style={{ paddingLeft: 16 }}>
            <ConfirmButton className='gx-mb-0 gx-mt-2' htmlType='submit' isLoading={loading}>
              {isEditModal ? 'Save' : 'Create'}
            </ConfirmButton>
            <CancelButton type='default' className='gx-mb-0 gx-mt-2' onClick={closeModal} loading={loading}>
              Cancel
            </CancelButton>
          </div>
        </Form>
      </Formik>
    </DefaultModal>
  );
};

export default DocumentModal;
